import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import styled, { css } from "styled-components";
import { useDebouncedCode } from "../../../hook/Subscription/useDebouncedCode";
import { useValidatePromo } from "../../../hook/Subscription/useValidatePromo";

/**
 * Promo code input with live backend validation.
 *
 * 4 states:
 *  - empty   : neutral
 *  - loading : spinner
 *  - valid   : green tint, ✓, discount badge, ✕ to clear
 *  - invalid : red border, error text below
 */
function PromoBox({ initialCode = "", id_project, onChange, lang = "th" }) {
  const [input, setInput] = useState(initialCode);
  const debounced = useDebouncedCode(input, 500);

  const { data, isLoading, error } = useValidatePromo({
    code: debounced,
    id_project,
  });

  useEffect(() => {
    if (data) onChange?.(data);
    else if (!input) onChange?.(null);
  }, [data, input, onChange]);

  const handleClear = () => {
    setInput("");
    onChange?.(null);
  };

  const isEmpty = !input;
  const isValid = !!data && !isLoading;
  const isInvalid = !!error && !isLoading && !!debounced;

  const discountLabel = data
    ? data.discount_type === "PERCENT"
      ? `−${data.discount_value}%`
      : `−${data.discount_value.toLocaleString()} ฿`
    : null;

  return (
    <Wrapper>
      <SectionLabel>{lang === "en" ? "PROMO CODE" : "รหัสส่วนลด"}</SectionLabel>

      <Field $valid={isValid} $invalid={isInvalid}>
        {isValid && <ValidIcon aria-hidden>✓</ValidIcon>}

        <Input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={lang === "en" ? "Enter code" : "ใส่รหัส"}
          readOnly={isValid}
          aria-invalid={isInvalid}
          $bold={isValid}
        />

        {isLoading && <Spinner animation="border" size="sm" variant="secondary" />}

        {discountLabel && <DiscountBadge>{discountLabel}</DiscountBadge>}

        {!isEmpty && !isLoading && (
          <ClearButton
            type="button"
            onClick={handleClear}
            aria-label={lang === "en" ? "Clear" : "ลบ"}
          >
            ✕
          </ClearButton>
        )}
      </Field>

      {isInvalid && (
        <ErrorText>
          {error?.message || (lang === "en" ? "Invalid code" : "รหัสไม่ถูกต้อง")}
        </ErrorText>
      )}
    </Wrapper>
  );
}

export default PromoBox;

// ─── Styled ──────────────────────────────────

const Wrapper = styled.div`
  padding: 0 16px 8px;
`;

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

// $valid / $invalid → transient props (won't leak to DOM)
const Field = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  transition: all 0.15s;

  ${({ $valid }) =>
    $valid &&
    css`
      border-color: #10b981;
      background: rgba(16, 185, 129, 0.08);
    `}

  ${({ $invalid }) =>
    $invalid &&
    css`
      border-color: #ef4444;
    `}

  &:focus-within {
    border-color: #6366f1;
  }
`;

const Input = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
  color: #111827;
  padding: 0;
  height: 100%;

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }

  &:read-only {
    cursor: default;
  }
`;

const ValidIcon = styled.span`
  color: #10b981;
  font-weight: 700;
  margin-right: 6px;
  flex-shrink: 0;
`;

const DiscountBadge = styled.span`
  font-size: 11px;
  color: #6b7280;
  margin-left: 8px;
  flex-shrink: 0;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 4px;
  margin-left: 4px;

  &:hover {
    color: #6b7280;
  }
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 11px;
  margin-top: 4px;
`;